import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CSSTransition } from 'react-transition-group';
import makeStyles from '@mui/styles/makeStyles';
import QuantitySelector, { qsDefaultMinWidth } from './quantity_selector';
import { defaultLineStyles } from 'styles/common';
import clsx from 'clsx';

const useStyles = makeStyles((theme) => ({
  inactiveButton: {
    position: 'absolute',
    top: 0,
    right: 0,
    color: 'white',
    font: 'inherit',
    border: 'none',
    cursor: 'pointer',
    height: '100%',
    width: '36px',
    borderRadius: '5px',
    '&:focus-visible': {
      outlineOffset: '2px',
      outline: `1px solid ${theme.color.blue}`,
    },
    '&:focus,&:hover': {
      backgroundColor:
        'var(--button-normal-variant-primary-rank-hover-state-background-color)',
    },
  },
  hidden: {
    display: 'none',
  },
  bgBlue: {
    backgroundColor: theme.color.blue,
  },
  bgGreen: {
    backgroundColor: theme.color.green,
    '&:hover': {
      backgroundColor: theme.color.greenDarker,
    },
  },
  line: {
    ...defaultLineStyles.line,
    background: 'white',
    borderColor: 'white',
  },
  line2: defaultLineStyles.lineVert,
}));

const useSelectorStyles = makeStyles(() => {
  const enterStyles = {
    maxWidth: '100%',
    marginLeft: '0%',
  };
  const exitStyles = (props) => ({
    maxWidth: `${props.selectorWidth}%`,
    marginLeft: `${100 - props.selectorWidth}%`,
    minWidth: `${(qsDefaultMinWidth / 100) * props.selectorWidth}px`,
  });
  const transitionStyles = (props) => ({
    transitionProperty: 'max-width, min-width, margin-left',
    transitionDuration: props.transitionTimeout,
    transitionTimingFunction: 'ease-out',
  });

  return {
    base: {
      boxSizing: 'border-box',
      height: '100%',
      position: 'absolute',
      top: 0,
      right: 0,
    },
    entering: (props) => ({
      ...enterStyles,
      ...transitionStyles(props),
    }),
    entered: enterStyles,
    exiting: (props) => ({
      ...exitStyles(props),
      ...transitionStyles(props),
    }),
    exited: (props) => ({
      visibility: 'hidden',
      position: 'absolute',
      ...exitStyles(props),
    }),
  };
});

const TransformingQuantitySelector = (props) => {
  const {
    value = 0,
    active = false,
    ariaQuantityName = '',
    onButtonClick = () => {},
    onOutsideClick = () => {},
  } = props;

  const selectorWidth = 85; // percent
  const transitionTimeout = 80; // ms
  const classes = useStyles(props);
  const selectorClasses = useSelectorStyles({
    selectorWidth,
    transitionTimeout: `${transitionTimeout}ms`,
  });
  const activeSelectorRef = useRef(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (!active) {
      return;
    }

    const handleClickOutside = (event) => {
      if (
        activeSelectorRef.current &&
        !activeSelectorRef.current.contains(event.target)
      ) {
        onOutsideClick();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    // eslint-disable-next-line consistent-return
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  });

  const inactiveContent = () =>
    value === 0 ? (
      <>
        <span className={classes.line} />
        <span className={`${classes.line} ${classes.line2}`} />
      </>
    ) : (
      value
    );

  const inactiveClasses = (state) => {
    const bgColor = value === 0 ? classes.bgBlue : classes.bgGreen;
    const hiddenClass = state !== 'exited' && classes.hidden;
    return clsx(classes.inactiveButton, bgColor, hiddenClass);
  };

  return (
    <CSSTransition in={active} timeout={transitionTimeout}>
      {(state) => (
        <>
          <QuantitySelector
            value={value}
            className={clsx(selectorClasses.base, selectorClasses[state])}
            ref={activeSelectorRef}
            {...props}
          />
          <button
            className={inactiveClasses(state)}
            onClick={onButtonClick}
            aria-label={t('common.quantitySelector.activator', {
              name: ariaQuantityName,
            })}
          >
            {inactiveContent()}
          </button>
        </>
      )}
    </CSSTransition>
  );
};

export default TransformingQuantitySelector;
